import { create } from 'zustand';

type Theme = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

interface ThemeState {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme, origin?: { x: number; y: number }) => void;
}

const STORAGE_KEY = 'pixel-machha-theme';

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(theme: Theme): ResolvedTheme {
  return theme === 'system' ? getSystemTheme() : theme;
}

function applyTheme(resolved: ResolvedTheme) {
  const root = document.documentElement;
  if (resolved === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

function loadSavedTheme(): Theme {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
  } catch {
    // localStorage unavailable
  }
  return 'dark'; // default to dark
}

const initialTheme = loadSavedTheme();
const initialResolved = resolveTheme(initialTheme);

// Apply theme immediately to avoid flash
applyTheme(initialResolved);

export const useThemeStore = create<ThemeState>((set) => ({
  theme: initialTheme,
  resolvedTheme: initialResolved,

  setTheme: (theme: Theme, origin?: { x: number; y: number }) => {
    const resolved = resolveTheme(theme);

    // Persist state — kept outside the View Transition callback so React's
    // async re-renders don't interfere with the DOM snapshot capture.
    const persist = () => {
      try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* unavailable */ }
      set({ theme, resolvedTheme: resolved });
    };

    if (typeof document === 'undefined') {
      applyTheme(resolved);
      persist();
      return;
    }

    if (origin && 'startViewTransition' in document) {
      // Circular reveal — only the synchronous DOM class flip goes inside the
      // callback so the "after" snapshot is captured cleanly.
      document.documentElement.style.setProperty('--reveal-x', `${origin.x}px`);
      document.documentElement.style.setProperty('--reveal-y', `${origin.y}px`);
      (document as Document & { startViewTransition: (cb: () => void) => void })
        .startViewTransition(() => applyTheme(resolved));
      persist();
    } else {
      // Smooth cross-fade fallback for Firefox / older browsers.
      document.documentElement.classList.add('theme-transitioning');
      applyTheme(resolved);
      persist();
      setTimeout(() => document.documentElement.classList.remove('theme-transitioning'), 350);
    }
  },
}));

// Listen for system theme changes when in 'system' mode
if (typeof window !== 'undefined') {
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.addEventListener('change', () => {
    const state = useThemeStore.getState();
    if (state.theme === 'system') {
      const resolved = getSystemTheme();
      applyTheme(resolved);
      useThemeStore.setState({ resolvedTheme: resolved });
    }
  });
}
